import {
    AmendmentSignatureStatus,
    AmendmentState,
    ContractSignatureStatus,
    ContractState,
    DocumentType,
    EarningType,
    ProfileType,
    Repetition,
    TerminationInfo,
} from "../../__generated__/graphql";
import {
    CoreMissionFieldsFragment,
    MissionAmendmentFieldsFragment,
} from "../../__generated__/operations/graphql";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Linking, StyleProp, StyleSheet, View, ViewStyle } from "react-native";
import { useTheme } from "../../hooks/use-theme";
import { useFormatContractWorkingTime, useI18n } from "../../hooks/i18n-hooks";
import {
    BalanceType,
    escapeMarkdown,
    formatMissionClientName,
    formatMissionWorkerName,
    getContractLabelKind,
    getIsPayVacationIndemnityWithSalary,
    getIsPay13thSalaryWithSalary,
    hasAppTimesheets,
    isOnDemandWorkingTime,
    openPhoneNumber,
} from "../../utils";
import {
    useBuildMissionDeductionDescription,
    useComputeAmenmentStatusDisplay,
    useMissionDisplayAttributes,
} from "../../hooks/missions";
import SalaryProjections from "../SalaryProjections";
import { isAfter } from "date-fns";
import ListItem from "../../ui_atoms/ListItem";
import ListHeader from "../../ui_atoms/ListHeader";
import { StackNavigationProp } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import Gap from "../../ui_atoms/Gap";
import MissionDocumentsList from "../document/MissionDocumentsList";
import { useContractCct } from "../../hooks/cct-utils";
import { useIntercom } from "../../hooks/intercom";
import VStack from "../../ui_atoms/VStack";
import { useMutation, useQuery } from "@apollo/client/react";
import { CHANGE_MISSION_OWNER, GET_MISSION, UPDATE_MISSION } from "../../graphql/core";
import { useSnackbar } from "../../hooks/snackbar";
import { IconName } from "../Icon";
import { useBuildIjmDescription } from "../../hooks/ijm";
import { SelectChooser, SelectItemType } from "../select/SelectChooser";
import MarkdownView from "../MarkdownView";
import { GET_CLIENT_USERS } from "../../graphql/client";
import { useCurrentProfile } from "../../hooks/use-current-profile";
import MissionDetailsStatus from "./MissionDetailsStatus";
import { useSetAlert } from "../../hooks/use-set-alert";
import HStack from "../../ui_atoms/HStack";
import Switch from "../../ui_atoms/Switch";
import { BodyMediumText } from "../FormattedText";
import { UserMissionsTabParamList } from "../../navigations/user/UserMissionsNavigation";
import { useDateFormatter, useFormatWithPeriod } from "../../hooks/date";
import Label from "../../ui_atoms/Label";
import { ArticleId } from "../../hooks/intercom/helpers";
import { useContractSigningInfo } from "../../hooks/contacts";

type BalanceInfoComponentProps = {
    i18nKey: string;
    icon: IconName;
    balanceStatus?: [boolean?, BalanceType?]; // [isFetching, balance]
};

const BalanceInfoComponent: React.FC<BalanceInfoComponentProps> = ({
    i18nKey,
    icon,
    balanceStatus = [undefined, undefined],
}) => {
    const i18n = useI18n();

    const [loading, balance] = balanceStatus;
    if (!balance) {
        return null;
    }
    const info = balance?.text;
    const infoDate = balance?.date;
    if (info == undefined || loading == undefined) {
        return null;
    }
    const tags = infoDate
        ? [i18n.t("mission.details.balance_date", { date: infoDate })]
        : undefined;
    return (
        <ListItem
            loading={loading}
            icon={icon}
            title={i18n.t(i18nKey)}
            description={info}
            tags={tags}
        />
    );
};

type MissionDetailsProps = {
    mission: CoreMissionFieldsFragment;
    terminationInfo?: TerminationInfo;
    style?: StyleProp<ViewStyle>;
    navigation?: StackNavigationProp<
        RootStackParamList & UserMissionsTabParamList,
        "MissionDetails"
    >;
    profileType: ProfileType;
    holidayBalance?: [boolean?, BalanceType?]; // [isFetching, balance]
    overtimeBalance?: [boolean?, BalanceType?]; // [isFetching, balance]
    onReadConfirmed?: (confirmed: boolean) => void;
    onUpdateMission: () => void;
    onEditMission: () => void;
    onDuplicateMission: () => void;
    readConfirmedError: boolean;
    missionAmendments: MissionAmendmentFieldsFragment[];
};

const MissionDetails: React.FC<MissionDetailsProps> = ({
    mission,
    terminationInfo,
    style,
    navigation,
    profileType,
    holidayBalance = undefined,
    overtimeBalance = undefined,
    onReadConfirmed = undefined,
    onUpdateMission,
    onEditMission,
    onDuplicateMission,
    readConfirmedError,
    missionAmendments,
}) => {
    const { marginSize, colors } = useTheme();
    const [updating, setUpdating] = React.useState(false);
    const styles = useStyles();
    const i18n = useI18n();
    const buildIjmDescription = useBuildIjmDescription();
    const { setPrompt } = useSetAlert();

    const [updateMission] = useMutation(UPDATE_MISSION);
    const [changeMissionOwner] = useMutation(CHANGE_MISSION_OWNER);
    const { showSnackbar } = useSnackbar();

    const currentProfile = useCurrentProfile();

    const { data: { getClientUsers = [] } = {} } = useQuery(GET_CLIENT_USERS, {
        variables: {
            clientId: currentProfile?.client?.id || "",
        },
        skip: !currentProfile?.client?.id,
    });
    const formatContractWorkingTime = useFormatContractWorkingTime();
    const getMissionDisplayAttributes = useMissionDisplayAttributes();
    const computeAmendmentStatusDisplay = useComputeAmenmentStatusDisplay();
    const { getStatusText } = useContractSigningInfo();
    const displayAttributes = useMemo(
        () => getMissionDisplayAttributes(mission, profileType, terminationInfo),
        [getMissionDisplayAttributes, mission, profileType, terminationInfo],
    );
    const [readConfirmed, setReadConfirmed] = React.useState(false);

    useEffect(() => {
        onReadConfirmed?.(readConfirmed);
    }, [readConfirmed, onReadConfirmed]);

    const hasAnnexes = useMemo(() => {
        return mission.documents.some((doc) => doc.type === DocumentType.MissionAnnex);
    }, [mission.documents]);
    const currentContract = useMemo(() => mission.currentContract, [mission.currentContract]);
    // BAS-2843: applying an amendment creates a new contract carrying the amendment document as its
    // signed asset, so `currentContract` would show the amendment PDF under the "employment
    // contract" entry and leave the initially signed contract unreachable. This always serves the
    // contract the mission started with — the same document as `currentContract` until an amendment
    // supersedes it — so the entry can simply always open it.
    const initialContractPdf = mission.initialContractPdf;
    // Once an amendment has been applied, the contract that PDF belongs to is signed by definition,
    // while `currentContract` describes the amended relationship rather than that document.
    const hasAppliedAmendment = useMemo(
        () => missionAmendments.some((amendment) => amendment.state === AmendmentState.Applied),
        [missionAmendments],
    );
    const missionCct = useContractCct(currentContract);

    const isPendingSignatureByCurrentUser =
        currentContract?.signatureStatus === ContractSignatureStatus.NeedSelfSign;

    const hasDelegatedSigner =
        !!mission.signerInfo && mission.signerInfo.id !== mission.clientInfo.id;
    // True while the querying profile still has to sign the contract (NeedSelfSign and
    // WaitingSelfSign are both relative to the user running the query).
    // BAS-2820: keep the signer assignment reachable while awaiting this signature, even when no
    // signer is set yet (e.g. mission created by a non-signing profile).
    const isAwaitingSelfSignature =
        !!currentContract?.signatureStatus &&
        [ContractSignatureStatus.NeedSelfSign, ContractSignatureStatus.WaitingSelfSign].includes(
            currentContract.signatureStatus,
        );

    const contractState = currentContract?.state;

    const contractLabelKind = getContractLabelKind(profileType, mission.clientInfo.useLse);
    const pdf_prefix = i18n.t(`contract.pdf_prefix.${contractLabelKind}`);
    const contract_name = i18n.t(`mission.contract_names.${contractLabelKind}`);

    const salaryDetails = useMemo<
        RootStackParamList["SalaryCountingDetails"]["salaryDetails"] | null
    >(() => {
        if (!currentContract) return null;

        return {
            invoiceSalary: currentContract.invoiceSalary || 0,
            grossSalary: currentContract.grossSalary || 0,
            netSalary: currentContract.netSalary || 0,
            deferredIndemnity: currentContract.deferredIndemnity,
            baseSalary: currentContract.baseSalary || 0,
            clientDeductions: currentContract.clientDeductions || [],
            workerDeductions: currentContract.workerDeductions || [],
            lseMargin: currentContract.lseMargin || 0,
            vacationCompensation: currentContract.vacationCompensation || 0,
            publicHolidaysCompensation: currentContract.publicHolidaysCompensation || 0,
            thirteenMonthCompensation: currentContract.thirteenMonthCompensation || 0,
        };
    }, [currentContract]);

    const contractEarningType = useMemo(
        () => currentContract?.earningType || EarningType.Monthly,
        [currentContract?.earningType],
    );

    const payrollBreakdownComponent = useMemo(
        () => (
            <ListItem
                labels={[{ text: i18n.t("mission.details.show_payroll_breakdown"), mode: "cta" }]}
                onPress={() => {
                    navigation?.navigate("PayrollBreakdownList", {
                        missionId: mission.id,
                    });
                }}
            />
        ),
        [i18n, mission.id, navigation],
    );

    const missionTimesheetsComponent = useMemo(
        () => (
            <ListItem
                labels={[{ text: i18n.t("timesheet_list.title"), mode: "cta" }]}
                onPress={() => {
                    navigation?.navigate("MissionTimeSheets", {
                        missionId: mission.id,
                    });
                }}
            />
        ),
        [i18n, mission.id, navigation],
    );

    const isContractValid = useMemo(
        () =>
            contractState &&
            [ContractState.Accepted, ContractState.Terminated, ContractState.Ended].includes(
                contractState,
            ),
        [contractState],
    );
    const formatWithPeriod = useFormatWithPeriod();

    const editMissionName = useCallback(() => {
        setPrompt({
            title: i18n.t("mission.edit_name"),
            message: i18n.t("mission.edit_name_description"),
            callbackOrButtons: (text) => {
                setUpdating(true);
                void updateMission({
                    variables: {
                        id: mission.id,
                        missionData: {
                            name: text,
                        },
                        generateContractAsset: false,
                    },
                    refetchQueries: [{ query: GET_MISSION, variables: { id: mission.id } }],
                })
                    .catch(() => {
                        showSnackbar({
                            text: i18n.t("mission.update_error"),
                            mode: "error",
                        });
                    })
                    .finally(() => {
                        setUpdating(false);
                    });
            },
            type: "plain-text",
            defaultValue: mission.name || "",
            keyboardType: "default",
        });
    }, [i18n, mission.id, mission.name, setPrompt, showSnackbar, updateMission]);

    const isMissionNameEditable = useMemo(
        () =>
            profileType === ProfileType.Client &&
            !updating &&
            contractState &&
            ![ContractState.Terminated, ContractState.Cancelled, ContractState.Ended].includes(
                contractState,
            ),
        [contractState, profileType, updating],
    );

    const { presentArticle, presentArticleId } = useIntercom();

    const [isResponsableChooserOpen, setIsResponsableChooserOpen] = React.useState(false);

    const ownersList = useMemo<SelectItemType<string>[]>(
        () =>
            getClientUsers.map((clientUser) => ({
                label: `${clientUser.firstName} ${clientUser.lastName}`,
                value: clientUser.id,
                useBigAvatarOrIcon: true,
                avatarPath: clientUser.avatarUrl,
            })),
        [getClientUsers],
    );

    const buildMissionDeductionDescription = useBuildMissionDeductionDescription();

    const hasNotAppliedYetAmendment = useMemo(
        () => missionAmendments.some((amendment) => amendment.state === AmendmentState.Accepted),
        [missionAmendments],
    );

    const lppName = useMemo(
        () => `LPP - ${currentContract?.lppPlan?.name}`,
        [currentContract?.lppPlan?.name],
    );

    const formattedDurationInfo = useMemo(() => {
        if (mission.currentContract?.state === ContractState.Terminated) {
            return `~~${displayAttributes.durationInfo}~~`;
        }

        return displayAttributes.durationInfo;
    }, [displayAttributes.durationInfo, mission.currentContract?.state]);

    const dateFormatter = useDateFormatter();

    const filteredAmendments = useMemo(() => {
        const nonDraftAmendments = missionAmendments
            .filter((amendment) => amendment.state !== AmendmentState.Draft)
            .slice()
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        const pendingAmendments = nonDraftAmendments.filter(
            (a) => a.state === AmendmentState.PendingAcceptation,
        );
        const acceptedAppliedCanceledAmendments = nonDraftAmendments.filter((a) =>
            [AmendmentState.Accepted, AmendmentState.Applied, AmendmentState.Canceled].includes(
                a.state,
            ),
        );
        const latestRejected = nonDraftAmendments.find((a) => a.state === AmendmentState.Rejected);

        const lastAccepted = acceptedAppliedCanceledAmendments[0];
        const showRejected =
            latestRejected &&
            pendingAmendments.length === 0 &&
            (!lastAccepted ||
                new Date(latestRejected.createdAt) > new Date(lastAccepted.createdAt));

        return [
            ...(showRejected ? [latestRejected] : []),
            ...pendingAmendments,
            ...acceptedAppliedCanceledAmendments,
        ];
    }, [missionAmendments]);

    const hasMoreThan3Amendments = useMemo(
        () => filteredAmendments.length > 3,
        [filteredAmendments],
    );

    const [showAllAmendments, setShowAllAmendments] = useState(false);

    // Prefer the client's dedicated contact details, falling back to the profile's personal
    // phone/email when no contact info is set on the client.
    const clientContactPhone =
        mission.clientInfo.contactPhoneNumber || mission.clientInfo.phoneNumber || undefined;
    const clientContactEmail =
        mission.clientInfo.contactEmail || mission.clientInfo.email || undefined;

    return (
        <View style={[styles.component, style]}>
            <VStack h="2/3" gap={1} style={{ marginVertical: marginSize }}>
                <MissionDetailsStatus mission={mission} terminationInfo={terminationInfo} />
            </VStack>
            {isContractValid &&
                (profileType === ProfileType.Client
                    ? payrollBreakdownComponent
                    : hasAppTimesheets(mission.payrollSource) && missionTimesheetsComponent)}

            {/* Information */}
            <ListHeader value={i18n.t("mission.details.mission_details")} />
            <ListItem
                icon="tag"
                title={i18n.t("mission.name")}
                description={mission.name || ""}
                {...(isMissionNameEditable
                    ? {
                          rightIcon: "edit",
                          rightActionMode: "action",
                          onPress: editMissionName,
                      }
                    : {})}
                loading={updating}
            />
            <ListItem
                icon="legal_employer"
                title={i18n.t("mission.legal_employer")}
                description={mission.legalEmployer?.marketingName || ""}
                labels={[{ text: i18n.t("know_more"), mode: "miniCta" }]}
                onPress={() => {
                    void presentArticle(`${mission.legalEmployer?.id}Info` as ArticleId);
                }}
                rightActionMode="none"
            />
            <ListItem
                icon="duration"
                title={i18n.t("mission.period")}
                description={formattedDurationInfo}
                labels={
                    terminationInfo?.cancellationRequest && displayAttributes.hasEffectiveEndDate
                        ? [{ text: displayAttributes.effectiveDurationInfo, mode: "redCta" }]
                        : []
                }
            />
            <ListItem
                icon="clock"
                title={i18n.t("mission.working_time")}
                description={formatContractWorkingTime(currentContract)}
            />
            <ListItem
                icon="work"
                title={i18n.t("mission.activity_name")}
                description={currentContract?.jobTitle || ""}
            />
            {currentContract?.jobDescription && (
                <ListItem
                    icon="onboarding"
                    title={i18n.t("mission.description")}
                    description={currentContract?.jobDescription}
                />
            )}
            {profileType === ProfileType.Client ? (
                <ListItem
                    icon="dollar-square"
                    title={i18n.t("mission.invoiced")}
                    description={displayAttributes.salaryInvoice}
                    onPress={
                        salaryDetails
                            ? () => {
                                  navigation?.navigate("SalaryCountingDetails", {
                                      earningType: contractEarningType,
                                      payVacationIndemnityWithSalary:
                                          getIsPayVacationIndemnityWithSalary(
                                              currentContract,
                                              mission.legalEmployer?.useLseCct,
                                          ),
                                      useLseCct: mission.legalEmployer?.useLseCct ?? true,
                                      pay13thSalaryWithSalary:
                                          getIsPay13thSalaryWithSalary(currentContract),
                                      salaryDetails,
                                  });
                              }
                            : undefined
                    }
                />
            ) : (
                <ListItem
                    icon="dollar-square"
                    title={i18n.t("mission.gross_salary")}
                    description={displayAttributes.salaryGross}
                    onPress={
                        salaryDetails
                            ? () => {
                                  navigation?.navigate("SalaryCountingDetails", {
                                      earningType: contractEarningType,
                                      payVacationIndemnityWithSalary:
                                          getIsPayVacationIndemnityWithSalary(
                                              currentContract,
                                              mission.legalEmployer?.useLseCct,
                                          ),
                                      useLseCct: mission.legalEmployer?.useLseCct ?? true,
                                      pay13thSalaryWithSalary:
                                          getIsPay13thSalaryWithSalary(currentContract),
                                      salaryDetails,
                                  });
                              }
                            : undefined
                    }
                />
            )}
            {currentContract?.ijmInfo && currentContract.ijmInfo.ijmGroup ? (
                <ListItem
                    icon="ijm"
                    title={i18n.t("ijm.mission.title")}
                    description={buildIjmDescription(
                        currentContract.ijmInfo.ijmGroup.name,
                        mission.legalEmployer.useLseCct
                            ? currentContract.ijmInfo.payFirstDays
                            : null,
                        currentContract.ijmInfo.payRemainingPercents,
                    )}
                    {...(profileType === ProfileType.Client
                        ? {
                              onPress: () => {
                                  if (currentContract.ijmInfo?.ijmGroup) {
                                      navigation?.navigate("IJMSelectionViewer", {
                                          ijmSelection: {
                                              ijmGroupId: currentContract.ijmInfo?.ijmGroup.id,
                                              ijmGroupName: currentContract.ijmInfo?.ijmGroup.name,
                                              ijmPayFirstDays:
                                                  currentContract.ijmInfo?.payFirstDays,
                                              ijmPayRemainingPercents:
                                                  currentContract.ijmInfo?.payRemainingPercents,
                                          },
                                      });
                                  }
                              },
                          }
                        : {
                              labels: [
                                  {
                                      text: i18n.t("know_more"),
                                      mode: "miniCtaBold",
                                      onPress: () => {
                                          void presentArticleId("10436709");
                                      },
                                  },
                              ],
                          })}
                />
            ) : (
                <ListItem
                    icon="ijm"
                    title={i18n.t("ijm.mission.title")}
                    description={i18n.t("ijm.no_ijm")}
                />
            )}
            {mission.payrollSource && (
                <ListItem
                    icon="calendar-clock"
                    title={i18n.t("mission.payrollSource")}
                    description={i18n.t(`payrollSource.type.${mission.payrollSource}`, {
                        defaultValue: mission.payrollSource,
                    })}
                />
            )}

            {displayAttributes.fees.map((fee) => (
                <ListItem {...fee} key={fee.key} />
            ))}

            {displayAttributes.deductions.length > 0 && (
                <ListItem
                    icon="receipt"
                    title={i18n.t("mission.details.mission_deductions")}
                    description={buildMissionDeductionDescription({
                        deductions: currentContract?.deductions,
                        isPackage:
                            currentContract?.workingTimeClassicSpec?.repetition === Repetition.None,
                    })}
                    onPress={() => {
                        navigation?.navigate("MissionInfoDeductions", {
                            deductions: displayAttributes.deductions,
                        });
                    }}
                />
            )}
            {holidayBalance && (
                <BalanceInfoComponent
                    i18nKey="mission.details.holiday_balance"
                    icon="vacation"
                    balanceStatus={holidayBalance}
                />
            )}
            {overtimeBalance && (
                <BalanceInfoComponent
                    i18nKey="mission.details.overtime_balance"
                    icon="clock"
                    balanceStatus={overtimeBalance}
                />
            )}
            {/* owner and signer */}
            {profileType === ProfileType.Client && !mission.clientInfo.isPrivateClient && (
                <>
                    <ListHeader value={i18n.t("mission.details.owner")} />
                    <ListItem
                        avatarPath={mission.clientInfo?.avatarUrl}
                        description={`${mission.clientInfo?.profileName}`}
                        rightIcon="right-left"
                        rightIconColor={colors.secondary}
                    />
                    <ListItem
                        labels={[{ text: i18n.t("mission.details.change_owner"), mode: "cta" }]}
                        rightIcon="right-left"
                        rightIconColor={colors.secondary}
                        rightActionMode="medium"
                        onPress={() => {
                            setIsResponsableChooserOpen(true);
                        }}
                    />
                    <SelectChooser<string>
                        topComponent={
                            <MarkdownView
                                markdown={i18n.t("mission.details.name_owner_description")}
                            />
                        }
                        visible={isResponsableChooserOpen}
                        onDismiss={() => {
                            setIsResponsableChooserOpen(false);
                        }}
                        searchItems={ownersList}
                        onSelect={async (newClientUser) => {
                            setIsResponsableChooserOpen(false);
                            if (newClientUser.value === mission.clientInfo?.id) {
                                return;
                            }
                            try {
                                await changeMissionOwner({
                                    variables: {
                                        missionId: mission.id,
                                        clientUserId: newClientUser.value,
                                    },
                                });
                                showSnackbar({
                                    mode: "success",
                                    text: i18n.t("mission.details.new_owner_selected"),
                                });
                            } catch {
                                showSnackbar({
                                    mode: "error",
                                    text: i18n.t("mission.update_error"),
                                });
                            }
                        }}
                        selectedValue={mission.clientInfo?.id}
                    />
                    {(hasDelegatedSigner || isAwaitingSelfSignature) && (
                        <>
                            <ListHeader value={i18n.t("mission.details.signer")} />
                            {hasDelegatedSigner && (
                                <ListItem
                                    avatarPath={mission.signerInfo?.avatarUrl}
                                    description={`${mission.signerInfo?.profileName}`}
                                    rightIcon="right-left"
                                    rightIconColor={colors.secondary}
                                />
                            )}
                            {isAwaitingSelfSignature && (
                                <ListItem
                                    labels={[
                                        {
                                            text: i18n.t("mission.details.change_signer"),
                                            mode: "cta",
                                        },
                                    ]}
                                    rightIcon="right-left"
                                    rightIconColor={colors.secondary}
                                    rightActionMode="medium"
                                    onPress={() => {
                                        navigation?.navigate("MissionSelectSignerScreen", {
                                            missionId: mission.id,
                                            showCurrentProfile: true,
                                        });
                                    }}
                                />
                            )}
                        </>
                    )}
                </>
            )}
            {/* display Worker info for the client */}
            {profileType === ProfileType.Client && (
                <>
                    <ListHeader value={i18n.t("mission.worker")} />
                    <ListItem
                        avatarPath={mission.workerInfo.avatarUrl}
                        description={formatMissionWorkerName(mission)}
                    />
                    {mission.workerInfo.phoneNumber && (
                        <ListItem
                            icon="phone-call"
                            description={mission.workerInfo.phoneNumber}
                            onPress={async () => {
                                if (mission.workerInfo.phoneNumber) {
                                    await openPhoneNumber(mission.workerInfo.phoneNumber);
                                }
                            }}
                            rightIcon="phone-call"
                            rightActionMode="action"
                        />
                    )}
                    {mission.workerInfo.email && (
                        <ListItem
                            icon="email"
                            description={mission.workerInfo.email}
                            onPress={async () => {
                                if (mission.workerInfo.email) {
                                    const url = `mailto:${mission.workerInfo.email}`;
                                    if (await Linking.canOpenURL(url)) {
                                        await Linking.openURL(url);
                                    }
                                }
                            }}
                            rightIcon="external-link"
                            rightActionMode="action"
                        />
                    )}
                </>
            )}
            {/* display Client info for the worker */}
            {profileType === ProfileType.Worker && (
                <>
                    <ListHeader value={i18n.t("mission.client")} />
                    <ListItem
                        avatarPath={mission.clientInfo.logoUrl}
                        description={formatMissionClientName(mission)}
                    />
                    {clientContactPhone && (
                        <ListItem
                            icon="phone-call"
                            description={clientContactPhone}
                            onPress={async () => {
                                await openPhoneNumber(clientContactPhone);
                            }}
                            rightIcon="phone-call"
                            rightActionMode="action"
                        />
                    )}
                    {clientContactEmail && (
                        <ListItem
                            icon="email"
                            description={clientContactEmail}
                            onPress={async () => {
                                const url = `mailto:${clientContactEmail}`;
                                if (await Linking.canOpenURL(url)) {
                                    await Linking.openURL(url);
                                }
                            }}
                            rightIcon="external-link"
                            rightActionMode="action"
                        />
                    )}
                </>
            )}
            {/* Documents */}
            <ListHeader value={i18n.t("mission.documents")} />

            {!isPendingSignatureByCurrentUser && (
                <ListItem
                    icon="file-pen"
                    description={contract_name}
                    badge={
                        <Label
                            mode={
                                hasAppliedAmendment
                                    ? "success"
                                    : displayAttributes.shortStatuslabelMode
                            }
                            text={
                                hasAppliedAmendment
                                    ? i18n.t("screens.user.contract_details.signed")
                                    : getStatusText(currentContract, true)
                            }
                        />
                    }
                    disabled={!initialContractPdf && !currentContract?.contractEffectiveAsset?.id}
                    onPress={() => {
                        if (initialContractPdf) {
                            navigation?.navigate("PdfViewer", {
                                title: contract_name,
                                filename: `${pdf_prefix}-${mission.id}`,
                                assetUrl: initialContractPdf,
                            });
                            return;
                        }
                        const assetId = currentContract?.contractEffectiveAsset?.id;
                        if (assetId) {
                            navigation?.navigate("PdfViewer", {
                                title: contract_name,
                                filename: `${pdf_prefix}-${currentContract?.id}`,
                                assetId: assetId,
                            });
                        }
                    }}
                />
            )}
            {currentContract?.lppPlan && (
                <ListItem
                    icon="user-shield"
                    description={lppName}
                    onPress={() => {
                        const assetUrl = currentContract?.lppPlan?.assetUrl;
                        if (assetUrl) {
                            navigation?.navigate("PdfViewer", {
                                assetUrl: assetUrl,
                                title: lppName,
                            });
                        }
                    }}
                />
            )}
            {!missionCct?.cctContractId?.startsWith("lift-no-cct") && (
                <ListItem
                    icon="cct"
                    description={i18n.t("mission.details.cct")}
                    onPress={() => {
                        if (currentContract?.cctDetails && missionCct) {
                            navigation?.navigate("CctDetails", {
                                cct: missionCct,
                                cctDetails: currentContract.cctDetails,
                                useLseCct: mission.legalEmployer.useLseCct,
                            });
                        }
                    }}
                />
            )}
            <MissionDocumentsList mission={mission} />
            {(showAllAmendments ? filteredAmendments : filteredAmendments.slice(0, 3)).map(
                (amendment) => {
                    const amendmentStatusDisplay = computeAmendmentStatusDisplay(amendment);
                    const statusText =
                        amendment.signatureStatus !== AmendmentSignatureStatus.NeedSelfSign &&
                        amendment.state === AmendmentState.PendingAcceptation
                            ? i18n.t("screens.user.contract_details.awaiting")
                            : amendmentStatusDisplay?.text;

                    const title = i18n.t("mission.details.amendment.title", {
                        creationDate: dateFormatter(amendment.createdAt),
                    });
                    return (
                        <ListItem
                            key={amendment.id}
                            icon="file-pen"
                            description={title}
                            tags={amendment.changes.map((amendmentChange) =>
                                i18n.t(`mission.details.amendment.${amendmentChange.field}`, {
                                    salaryAmount:
                                        amendmentChange.field === "grossSalary"
                                            ? formatWithPeriod(
                                                  amendmentChange.newValue
                                                      ? parseInt(amendmentChange.newValue)
                                                      : 0,
                                                  currentContract?.earningType || EarningType.Daily,
                                              )
                                            : undefined,
                                    contractEndDate:
                                        amendmentChange.field === "endDate"
                                            ? dateFormatter(amendmentChange.newValue || "")
                                            : undefined,
                                }),
                            )}
                            badge={<Label mode={amendmentStatusDisplay?.mode} text={statusText} />}
                            tagsMode="vertical"
                            disabled={!amendment.amendmentEffectiveAsset?.id}
                            onPress={() => {
                                const assetId = amendment.amendmentEffectiveAsset?.id;
                                if (assetId) {
                                    if (
                                        amendment.state === AmendmentState.PendingAcceptation &&
                                        !mission.terminationInfo?.cancellationRequest
                                    ) {
                                        navigation?.navigate("ContractPreviewScreen", {
                                            contractId: amendment.id,
                                            contractType: "amendment",
                                        });
                                    } else {
                                        navigation?.navigate("PdfViewer", {
                                            assetId: assetId,
                                            title: title,
                                            topMarkdown: amendment.rejectedReason
                                                ? {
                                                      text: i18n.t(
                                                          "mission.details.amendment.refused_amendment",
                                                          {
                                                              reason: escapeMarkdown(
                                                                  amendment.rejectedReason,
                                                              ),
                                                          },
                                                      ),
                                                      color: colors.error,
                                                  }
                                                : amendment.canceledReason
                                                  ? {
                                                        text: i18n.t(
                                                            "mission.details.amendment.canceled_amendment",
                                                            {
                                                                reason: escapeMarkdown(
                                                                    amendment.canceledReason,
                                                                ),
                                                            },
                                                        ),
                                                        color: colors.error,
                                                    }
                                                  : undefined,
                                        });
                                    }
                                }
                            }}
                        />
                    );
                },
            )}
            {hasMoreThan3Amendments && !showAllAmendments && (
                <ListItem
                    icon="file-pen"
                    iconColor={colors.secondary}
                    globalTextColor={colors.secondary}
                    description={i18n.t("mission.details.amendment.see_more")}
                    onPress={() => setShowAllAmendments(true)}
                />
            )}
            {/* Projection: none on demand, where no volume is guaranteed */}
            {!(
                currentContract?.effectiveEndDate &&
                isAfter(new Date(), new Date(currentContract.effectiveEndDate))
            ) &&
                !isOnDemandWorkingTime(currentContract?.workingTimeClassicSpec) && (
                    <>
                        <Gap gap={1} />
                        <ListHeader value={i18n.t("mission.salary_projections")} />
                        <Gap gap={0.5} />
                        <SalaryProjections mission={mission} />
                    </>
                )}
            <Gap gap={1} />

            {/* Actions */}
            {isContractValid && profileType === ProfileType.Worker && payrollBreakdownComponent}
            {displayAttributes.canEdit && (
                <ListItem
                    labels={[
                        {
                            text: i18n.t("mission.mission_edit"),
                            mode: "cta",
                            color:
                                displayAttributes.isMissionAwaitingWorkerSignature ||
                                displayAttributes.hasExistingPendingAvenant ||
                                hasNotAppliedYetAmendment
                                    ? colors.fieldLabel
                                    : undefined,
                        },
                    ]}
                    onPress={onEditMission}
                />
            )}
            {displayAttributes.canUpdate && (
                <ListItem
                    labels={[
                        {
                            text: i18n.t("mission.mission_edit"),
                            mode: "cta",
                        },
                    ]}
                    onPress={onUpdateMission}
                />
            )}
            {displayAttributes.canDuplicate && (
                <ListItem
                    labels={[{ text: i18n.t("mission.mission_duplicate"), mode: "cta" }]}
                    onPress={onDuplicateMission}
                />
            )}
            <ListItem
                labels={[{ text: i18n.t("mission.details.mission_history"), mode: "cta" }]}
                onPress={() => {
                    navigation?.navigate("MissionLogs", { missionId: mission.id });
                }}
            />
            {displayAttributes.canCancelOrTerminate && !terminationInfo?.cancellationRequest && (
                <ListItem
                    labels={[
                        {
                            text: i18n.t(
                                `mission.${terminationInfo?.newState === ContractState.Cancelled ? "cancel" : "terminate"}_mission`,
                            ),
                            mode: "redCta",
                        },
                    ]}
                    rightActionMode="empty"
                    onPress={() => {
                        navigation?.navigate("MissionTermination", {
                            missionId: mission.id,
                        });
                    }}
                />
            )}
            {/* BAS-2762: only ask the worker to confirm reading the annexes while the contract
                still awaits their signature; hide it once the contract has been signed. */}
            {hasAnnexes && profileType === ProfileType.Worker && isAwaitingSelfSignature && (
                <HStack gap="1/2" v="1" h="2/3" wrapChildren={false}>
                    <Switch
                        value={readConfirmed}
                        onChange={setReadConfirmed}
                        trackColor={{ false: readConfirmedError ? colors.error : undefined }}
                    />
                    <BodyMediumText
                        color={readConfirmedError ? colors.error : undefined}
                        style={{ flex: 1 }}
                    >
                        {i18n.t("mission.details.confirm_read_annexes")}
                    </BodyMediumText>
                </HStack>
            )}
            <Gap gap={5} />
        </View>
    );
};

const useStyles = () => {
    const { colors, fonts } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                component: {
                    padding: 0,
                    display: "flex",
                    flex: 1,
                },
                scrollView: {
                    width: "100%",
                },
                flatView: {
                    alignSelf: "stretch",
                    flex: 0,
                    flexDirection: "column",
                    alignItems: "stretch",
                },
                documentsListGroup: {
                    flex: 1,
                    alignItems: "flex-end",
                    justifyContent: "flex-start",
                },
                errorText: {
                    color: colors.error,
                },
                reasonText: {
                    color: colors.onSurfaceDisabled,
                },
                info: {
                    ...fonts.bodyMedium,
                    color: colors.onSurfaceDisabled,
                },
            }),
        [colors.error, colors.onSurfaceDisabled, fonts.bodyMedium],
    );
};

export default MissionDetails;
